// Studio-grade lecture motion + narration-synced cueing helpers.
//
// Two concerns live here, both pure and dependency-free so they're trivially
// testable and reusable across the player components:
//   1. Motion — prefers-reduced-motion detection + the slide-transition class
//      set, so SlideStage and friends share one source of truth for timing.
//   2. Cueing — derive the `cue` object a slide's interactive widget consumes
//      from live audio progress + the widget's own config. The widget contract
//      (consumed on the lab side) is intentionally small and all-optional so a
//      widget that ignores `cue` keeps working unchanged.

import type { WidgetRef } from '../services/lectureApi';

// ── Motion ───────────────────────────────────────────────────────────────────

/** True when the user has asked the OS to minimise non-essential motion. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Slide cross-fade + subtle rise. Duration is short enough to feel responsive
// when clicking through quickly, long enough to read as a deliberate dissolve.
// Reduced-motion collapses to an instant swap (no transform, no fade delay).
export const SLIDE_TRANSITION_MS = 360;

/**
 * Tailwind class set for the active slide layer. `entering` drives the
 * mount-time enter animation; when reduced motion is requested we return a
 * static class so the swap is instantaneous. The enter state starts slightly
 * lowered, dimmed, and barely scaled-down so the slide *blooms* into place —
 * cinematic without being slow.
 */
export function slideLayerClass(entering: boolean, reduced: boolean): string {
  if (reduced) return 'opacity-100';
  return entering
    ? 'opacity-0 translate-y-2 scale-[0.985]'
    : 'opacity-100 translate-y-0 scale-100';
}

// ── Cueing ───────────────────────────────────────────────────────────────────

// The contract a slide's widget receives. Every field is optional; a widget may
// honour any subset (or none). Kept in lockstep with the lab WidgetRenderer.
export interface WidgetCue {
  /** 0–1: fraction of the slide's narration elapsed. */
  progress?: number;
  /** Symbol(s) / token(s) the narration is currently talking about. */
  highlight?: string | string[];
  /** Discrete step for stepwise widgets (0-based), advanced by narration. */
  step?: number;
  /** Mirrors the transport: true while the slide's audio is playing. */
  play?: boolean;
}

// Pull a plausible config object off a widget ref. Slides carry config under
// `widget.config`, but some refs are flattened — accept either, never throw.
function readConfig(widget: WidgetRef | undefined): Record<string, unknown> {
  if (!widget) return {};
  const nested = (widget as { config?: unknown }).config;
  if (nested && typeof nested === 'object') return nested as Record<string, unknown>;
  return widget as Record<string, unknown>;
}

// A per-step cue an author can attach to a widget so the narration can spotlight
// exactly what it's describing at each beat. We accept several lenient shapes:
//   cue_steps: [{ at?: 0..1, highlight?, step? }, ...]   (preferred)
//   cue_steps: ["O", ["Na","Cl"], ...]                   (bare highlight list)
//   steps:     [{ highlight? }, ...]                     (reuse the steps array)
// `at` is an optional 0..1 fraction marking when that step begins; when absent
// the steps are assumed evenly spaced across the narration.
interface CueStep {
  at?: number;
  highlight?: string | string[];
  step?: number;
}

function coerceHighlight(value: unknown): string | string[] | undefined {
  if (typeof value === 'string' && value) return value;
  if (Array.isArray(value) && value.length) {
    const strs = value.filter((v): v is string => typeof v === 'string' && !!v);
    if (strs.length) return strs;
  }
  return undefined;
}

// Read an explicit per-step cue track from config, normalised to CueStep[].
// Returns undefined when no usable track is present.
function readCueSteps(config: Record<string, unknown>): CueStep[] | undefined {
  const raw = config.cue_steps ?? config.cueSteps ?? config.cues;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const steps: CueStep[] = raw.map((entry, i): CueStep => {
    // Bare highlight (string or string[]) → that step's highlight.
    const bare = coerceHighlight(entry);
    if (bare !== undefined) return { highlight: bare };
    if (entry && typeof entry === 'object') {
      const o = entry as Record<string, unknown>;
      const at = typeof o.at === 'number' ? Math.max(0, Math.min(1, o.at)) : undefined;
      const step = typeof o.step === 'number' ? Math.floor(o.step) : i;
      return { at, step, highlight: coerceHighlight(o.highlight) };
    }
    return { step: i };
  });
  return steps;
}

// Highlight info a slide author can put on a widget's config to spotlight the
// thing the instructor is currently describing — used when there is no
// time-resolved per-step cue track (a single, static highlight for the slide).
function readStaticHighlight(
  config: Record<string, unknown>,
): string | string[] | undefined {
  const symbols = coerceHighlight(config.highlightSymbols);
  if (symbols !== undefined) return symbols;
  const category = config.highlightCategory;
  if (typeof category === 'string' && category) return category;
  return coerceHighlight(config.highlight);
}

// Widget types that advance through discrete steps as the instructor narrates.
// For these we map narration progress → a step index so the visual keeps pace
// with the spoken explanation. The count comes from config where the author
// declared it (steps / totalSteps), else a sensible per-type default.
const STEPWISE_DEFAULT_STEPS: Record<string, number> = {
  equation_balancer: 4,
  electron_configuration: 6,
  bohr_atom: 4,
  chemical_formula: 3,
  percent_composition: 3,
};

function readStepCount(type: string, config: Record<string, unknown>): number | undefined {
  const declared = config.steps ?? config.totalSteps ?? config.stepCount;
  if (typeof declared === 'number' && declared > 0) return Math.floor(declared);
  if (Array.isArray(declared) && declared.length) return declared.length;
  return STEPWISE_DEFAULT_STEPS[type];
}

// Given the (possibly time-stamped) cue track and current progress, find the
// active step index. When steps carry explicit `at` marks we pick the last one
// whose mark has been passed; otherwise the steps are spaced evenly across the
// narration. Always returns a valid index into `steps`.
function activeStepIndex(steps: CueStep[], progress: number): number {
  const hasMarks = steps.some((s) => typeof s.at === 'number');
  if (hasMarks) {
    let idx = 0;
    for (let i = 0; i < steps.length; i++) {
      const at = steps[i].at;
      if (typeof at === 'number' && progress >= at) idx = i;
    }
    return idx;
  }
  // Evenly spaced: the last step lands exactly as the narration completes.
  return Math.min(steps.length - 1, Math.floor(progress * steps.length));
}

/**
 * Compute the cue for a slide's primary widget from live transport state.
 *
 * @param widget       the slide's widget ref (config carries highlight/steps)
 * @param currentTime  audio elapsed, seconds
 * @param duration     audio total, seconds (manifest or element)
 * @param isPlaying    transport playing flag
 *
 * Returns `undefined` when there's nothing meaningful to drive (no widget),
 * so callers can pass it straight through without conditional plumbing.
 *
 * Resolution order:
 *   • progress  — currentTime/duration, clamped 0..1 (0 when duration unknown);
 *   • step      — from a per-step `cue_steps` track if present (honouring its
 *                 optional `at` time-marks), else evenly from a declared/default
 *                 step count for stepwise widget types;
 *   • highlight — the active per-step highlight from the cue track if present,
 *                 else the slide's static highlight config;
 *   • play      — mirrors `isPlaying`.
 *
 * Signature and the `WidgetCue` return shape are unchanged; this is a purely
 * additive enrichment — a widget with no cue config still gets `{ progress,
 * play }` exactly as before.
 */
export function computeCue(
  widget: WidgetRef | undefined,
  currentTime: number,
  duration: number,
  isPlaying: boolean,
): WidgetCue | undefined {
  if (!widget) return undefined;

  // No audio transport for this slide (narration not synthesized yet, or the
  // duration is unknown/non-finite): the narration can't drive the visual in
  // lockstep, so return an INERT cue. That lets the widget fall back to its own
  // self-demo and keeps its manual play/step controls live — otherwise a
  // `play:false` (re)issued every render would re-pause the widget, making its
  // in-stage Play button appear dead. Slides that DO have audio are unaffected.
  if (!(duration > 0) || !isFinite(duration)) return {};

  const safeTime = typeof currentTime === 'number' && isFinite(currentTime) ? currentTime : 0;
  const progress =
    duration > 0 && isFinite(duration)
      ? Math.max(0, Math.min(1, safeTime / duration))
      : 0;

  const config = readConfig(widget);
  const type = (widget.type as string) ?? '';

  const cue: WidgetCue = { progress, play: isPlaying };

  // Prefer a time-resolved per-step cue track: it drives BOTH the step index
  // and the highlight, keeping the widget in lockstep with the spoken beat.
  const cueSteps = readCueSteps(config);
  if (cueSteps) {
    const idx = activeStepIndex(cueSteps, progress);
    const active = cueSteps[idx];
    // Honour an author-declared step number on the entry; else use the index.
    cue.step = typeof active.step === 'number' ? active.step : idx;
    const h = active.highlight ?? readStaticHighlight(config);
    if (h !== undefined) cue.highlight = h;
    return cue;
  }

  // No explicit track: static highlight (if any) for the whole slide.
  const highlight = readStaticHighlight(config);
  if (highlight !== undefined) cue.highlight = highlight;

  // Map progress → step for stepwise widgets. The last step is reached just
  // before the narration ends, so the visual lands fully assembled on-cue.
  const stepCount = readStepCount(type, config);
  if (stepCount && stepCount > 0) {
    cue.step = Math.min(stepCount - 1, Math.floor(progress * stepCount));
  }

  return cue;
}
